import { useCallback, useEffect, useRef, useState } from 'react';

export const useIframeMessaging = (iframeRef, iFrameOrigin, initialMessages, iFramePagePath, language) => {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const messageQueue = useRef([]);

  const sendMessage = useCallback((messageType, messageValue) => {
    const message = {
      type: messageType,
      value: messageValue
    };

    if (isIframeLoaded && iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage(message, iFrameOrigin);
    } else {
      messageQueue.current.push(message);
      console.log("iframe not ready, message queued");
    }
  }, [iFrameOrigin, iframeRef, isIframeLoaded]);

  const handleLoad = useCallback(() => {
    setIsIframeLoaded(true);
  }, []);

  useEffect(() => {
    if (isIframeLoaded) {
      setTimeout(() => {
        initialMessages.forEach(message => sendMessage(message.type, message.value));
        while (messageQueue.current.length > 0) {
          const message = messageQueue.current.shift();
          sendMessage(message.type, message.value);
        }
        //console.log('All queued messages sent');
      }, 100);
     
    }
    return () => {
      setIsIframeLoaded(false );
    }
  }, [isIframeLoaded, initialMessages, sendMessage]);
  
  
  useEffect(() => {
    const handleMessage = (event) => {

      // Make sure the message is coming from the expected origin
      // During development, we might be more lenient about origins
      // Remove trailing slash from iFrameOrigin for comparison
      const normalizedOrigin = iFrameOrigin.endsWith('/') ? iFrameOrigin.slice(0, -1) : iFrameOrigin;
      const normalizedEventOrigin = event.origin.endsWith('/') ? event.origin.slice(0, -1) : event.origin;

      if (normalizedEventOrigin !== normalizedOrigin && normalizedOrigin !== '*') {
        //console.warn(`Message rejected: Origin ${event.origin} doesn't match expected ${iFrameOrigin}`);
        return;
      }

      // Basic validation
      if (!event.data || typeof event.data !== 'object' || !event.data.type) {
        return;
      }

      // Handle specific message types
      if (event.data.type === 'navigate') {
        window.location.href = event.data.path;
      }
      
      // Always dispatch the custom event for any message from iframe
      // This allows other components to listen for all message types
      const customEvent = new CustomEvent('iframe-message', { 
        detail: event.data 
      });
      window.dispatchEvent(customEvent);
      
      // Log the dispatched event for debugging
      //console.log('Dispatched iframe-message event:', event.data);
    };

    window.addEventListener('message', handleMessage);
    //console.log('Message listener attached');

    return () => {
      window.removeEventListener('message', handleMessage);
      setIsIframeLoaded(false);
    };
  }, [iFrameOrigin, iFramePagePath, language]);

  return { sendMessage, handleLoad };
};